import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Trophy, MapPin, Calendar, Clock, ChevronLeft, ChevronRight,
  Dog, Star, Users, Camera, ExternalLink, User, LogIn,
  Award, Medal, Crown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import LanguageSwitcher from '../components/LanguageSwitcher';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Sample dog shows for demo
const DEMO_SHOWS = [
  {
    id: 'show_demo1',
    name: 'Sydney Royal Dog Show 2025',
    description: 'Australia\'s premier dog show featuring over 3,000 dogs from 200 breeds. Includes Best in Show, Junior Handler, and Breed Group competitions.',
    date: '2025-04-12T09:00:00Z',
    end_date: '2025-04-14T17:00:00Z',
    location: {
      venue: 'Sydney Showground',
      address: '1 Showground Rd',
      suburb: 'Sydney Olympic Park',
      state: 'NSW'
    },
    organizer: 'Dogs NSW',
    website: 'https://dogsnsw.org.au',
    entry_fee: 45,
    breed_groups: ['Toys', 'Terriers', 'Gundogs', 'Hounds', 'Working', 'Utility', 'Non-Sporting'],
    image_url: 'https://images.pexels.com/photos/5732528/pexels-photo-5732528.jpeg?auto=compress&cs=tinysrgb&w=800',
    status: 'upcoming',
    featured: true
  },
  {
    id: 'show_demo2',
    name: 'Melbourne Dog Lovers Show',
    description: 'The ultimate event for dog lovers! Meet hundreds of breeds, shop pet products, and watch amazing performances.',
    date: '2025-05-03T10:00:00Z',
    end_date: '2025-05-04T17:00:00Z',
    location: {
      venue: 'Melbourne Convention Centre',
      address: '1 Convention Centre Pl',
      suburb: 'South Wharf',
      state: 'VIC'
    },
    organizer: 'Pet Expos Australia',
    entry_fee: 35,
    breed_groups: ['All Breeds'],
    image_url: 'https://images.pexels.com/photos/7210754/pexels-photo-7210754.jpeg?auto=compress&cs=tinysrgb&w=800',
    status: 'upcoming',
    featured: true
  },
  {
    id: 'show_demo3',
    name: 'Brisbane Championship Dog Show',
    description: 'Queensland\'s largest championship show with conformation, obedience, and agility competitions.',
    date: '2025-03-22T08:00:00Z',
    end_date: '2025-03-23T16:00:00Z',
    location: {
      venue: 'Durack Showgrounds',
      address: 'Cnr Blunder Rd & Inala Ave',
      suburb: 'Durack',
      state: 'QLD'
    },
    organizer: 'Dogs Queensland',
    entry_fee: 40,
    breed_groups: ['Toys', 'Terriers', 'Gundogs', 'Hounds', 'Working'],
    image_url: 'https://images.pexels.com/photos/5749131/pexels-photo-5749131.jpeg?auto=compress&cs=tinysrgb&w=800',
    status: 'upcoming',
    featured: false
  }
];

// Sample results for completed shows
const DEMO_RESULTS = [
  {
    id: 'result_1',
    show_id: 'show_completed',
    category: 'Best in Show',
    placement: 1,
    dog_name: 'Champion Dogtastic Gold Rush',
    breed: 'Golden Retriever',
    owner: 'Sarah Mitchell',
    image: 'https://images.pexels.com/photos/2253275/pexels-photo-2253275.jpeg?auto=compress&cs=tinysrgb&w=400'
  },
  {
    id: 'result_2',
    show_id: 'show_completed',
    category: 'Best in Show',
    placement: 2,
    dog_name: 'Supreme Ch Moonlight Sonata',
    breed: 'Poodle (Standard)',
    owner: 'James Wilson'
  },
  {
    id: 'result_3',
    show_id: 'show_completed',
    category: 'Best in Show',
    placement: 3,
    dog_name: 'Grand Ch Royal Flush',
    breed: 'Border Collie',
    owner: 'Emma Thompson'
  }
];

// Show Card Component
const ShowCard = ({ show, onViewDetails }) => {
  const date = new Date(show.date);
  const endDate = show.end_date ? new Date(show.end_date) : null;
  
  const formatDate = (d) => d.toLocaleDateString('en-AU', { 
    weekday: 'short', 
    day: 'numeric', 
    month: 'short' 
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl overflow-hidden shadow-[0_4px_20px_rgba(0,0,0,0.06)] border border-[#EBE8E0]"
    >
      <div className="relative h-48">
        <img
          src={show.image_url || 'https://images.pexels.com/photos/5732528/pexels-photo-5732528.jpeg'}
          alt={show.name}
          className="w-full h-full object-cover"
        />
        {show.featured && (
          <div className="absolute top-3 left-3 bg-[#F4D35E] text-[#1A1817] text-xs px-2 py-1 rounded-full font-semibold flex items-center gap-1">
            <Star className="w-3 h-3" fill="currentColor" /> Featured
          </div>
        )}
        <div className={`absolute top-3 right-3 text-xs px-2 py-1 rounded-full font-medium ${
          show.status === 'upcoming' ? 'bg-[#4E876C] text-white' :
          show.status === 'ongoing' ? 'bg-[#FF7555] text-white' :
          'bg-[#7A7571] text-white'
        }`}>
          {show.status === 'upcoming' ? 'Upcoming' : show.status === 'ongoing' ? 'Live Now' : 'Completed'}
        </div>
      </div>

      <div className="p-4">
        <h3 className="text-lg font-bold text-[#1A1817] mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
          {show.name}
        </h3>

        <div className="space-y-2 text-sm text-[#7A7571] mb-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[#FF7555]" />
            {formatDate(date)}
            {endDate && ` - ${formatDate(endDate)}`}
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-[#FF7555]" />
            {show.location?.venue}, {show.location?.state}
          </div>
          {show.entry_fee && (
            <div className="flex items-center gap-2">
              <Trophy className="w-4 h-4 text-[#FF7555]" />
              Entry: ${show.entry_fee}
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-1 mb-4">
          {show.breed_groups?.slice(0, 3).map(group => (
            <span key={group} className="text-xs bg-[#F5F3EE] text-[#7A7571] px-2 py-1 rounded-full">
              {group}
            </span>
          ))}
          {show.breed_groups?.length > 3 && (
            <span className="text-xs text-[#7A7571]">+{show.breed_groups.length - 3} more</span>
          )}
        </div>

        <Button
          onClick={() => onViewDetails(show)}
          variant="outline"
          className="w-full rounded-xl border-[#EBE8E0] hover:bg-[#F5F3EE]"
        >
          View Details
        </Button>
      </div>
    </motion.div>
  );
};

// Show Details Modal
const ShowDetailsModal = ({ show, isOpen, onClose, user }) => {
  const navigate = useNavigate();
  
  if (!isOpen || !show) return null;

  const date = new Date(show.date);
  const endDate = show.end_date ? new Date(show.end_date) : null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="relative h-64">
          <img
            src={show.image_url || 'https://images.pexels.com/photos/5732528/pexels-photo-5732528.jpeg'}
            alt={show.name}
            className="w-full h-full object-cover"
          />
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          <h2 className="text-2xl font-bold text-[#1A1817] mb-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            {show.name}
          </h2>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="flex items-center gap-2 text-[#7A7571]">
              <Calendar className="w-5 h-5 text-[#FF7555]" />
              <div>
                <p className="text-sm font-medium text-[#1A1817]">
                  {date.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
                {endDate && (
                  <p className="text-xs">to {endDate.toLocaleDateString('en-AU', { day: 'numeric', month: 'long' })}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 text-[#7A7571]">
              <MapPin className="w-5 h-5 text-[#FF7555]" />
              <div>
                <p className="text-sm font-medium text-[#1A1817]">{show.location?.venue}</p>
                <p className="text-xs">{show.location?.suburb}, {show.location?.state}</p>
              </div>
            </div>
          </div>

          <p className="text-[#7A7571] mb-4">{show.description}</p>

          <div className="mb-4">
            <h4 className="font-semibold text-[#1A1817] mb-2">Breed Groups</h4>
            <div className="flex flex-wrap gap-2">
              {show.breed_groups?.map(group => (
                <span key={group} className="text-sm bg-[#FF7555]/10 text-[#FF7555] px-3 py-1 rounded-full">
                  {group}
                </span>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between p-4 bg-[#F5F3EE] rounded-xl mb-4">
            <div>
              <p className="text-sm text-[#7A7571]">Entry Fee</p>
              <p className="text-2xl font-bold text-[#1A1817]">${show.entry_fee}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-[#7A7571]">Organized by</p>
              <p className="font-medium text-[#1A1817]">{show.organizer}</p>
            </div>
          </div>

          <div className="flex gap-3">
            {show.website && (
              <a
                href={show.website}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1"
              >
                <Button variant="outline" className="w-full rounded-xl border-[#EBE8E0]">
                  <ExternalLink className="w-4 h-4 mr-2" /> Website
                </Button>
              </a>
            )}
            <Button
              onClick={() => {
                if (!user) {
                  toast.error('Sign in to register');
                  return;
                }
                toast.success('Registration coming soon!');
              }}
              className="flex-1 bg-[#FF7555] hover:bg-[#E5684A] text-white rounded-xl"
            >
              <Trophy className="w-4 h-4 mr-2" /> Register
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Results Section
const ResultsSection = ({ results }) => {
  const placementIcon = (p) => {
    if (p === 1) return <Crown className="w-5 h-5 text-[#F4D35E]" />;
    if (p === 2) return <Medal className="w-5 h-5 text-[#C0C0C0]" />;
    if (p === 3) return <Award className="w-5 h-5 text-[#CD7F32]" />;
    return <span className="w-5 h-5 text-center font-bold">{p}</span>;
  };

  return (
    <div className="bg-white rounded-2xl p-6 shadow-[0_4px_20px_rgba(0,0,0,0.06)] border border-[#EBE8E0]">
      <h3 className="text-xl font-bold text-[#1A1817] mb-4 flex items-center gap-2" style={{ fontFamily: "'Fredoka', sans-serif" }}>
        <Trophy className="w-6 h-6 text-[#F4D35E]" /> Latest Results
      </h3>
      
      <div className="space-y-3">
        {results.map(result => (
          <div
            key={result.id}
            className="flex items-center gap-4 p-3 bg-[#F5F3EE] rounded-xl"
          >
            <div className="w-10 h-10 flex items-center justify-center">
              {placementIcon(result.placement)}
            </div>
            {result.image ? (
              <img src={result.image} alt={result.dog_name} className="w-12 h-12 rounded-full object-cover" />
            ) : (
              <div className="w-12 h-12 bg-[#FF7555]/10 rounded-full flex items-center justify-center">
                <Dog className="w-6 h-6 text-[#FF7555]" />
              </div>
            )}
            <div className="flex-1">
              <p className="font-semibold text-[#1A1817]">{result.dog_name}</p>
              <p className="text-sm text-[#7A7571]">{result.breed} • {result.owner}</p>
            </div>
            <span className="text-xs bg-[#F4D35E]/20 text-[#B8960A] px-2 py-1 rounded-full font-medium">
              {result.category}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Main Dog Shows Page
export default function PupShows() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [selectedShow, setSelectedShow] = useState(null);
  const [filter, setFilter] = useState('upcoming');

  useEffect(() => {
    // Check auth
    fetch(`${API_URL}/api/puppoto/auth/me`, { credentials: 'include' })
      .then(res => res.ok ? res.json() : null)
      .then(data => setUser(data))
      .catch(() => {});

    // Fetch shows
    fetchShows();
  }, [filter]);

  const fetchShows = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/puppoto/shows?upcoming_only=${filter === 'upcoming'}`);
      if (res.ok) {
        const data = await res.json();
        setShows(data.shows?.length > 0 ? data.shows : DEMO_SHOWS);
      } else {
        setShows(DEMO_SHOWS);
      }
    } catch (error) {
      setShows(DEMO_SHOWS);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/puppoto/shows';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]" style={{ fontFamily: "'Figtree', sans-serif" }}>
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-[#EBE8E0]">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <a href="/puppoto" className="flex items-center gap-2">
            <ChevronLeft className="w-5 h-5 text-[#7A7571]" />
          </a>
          
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-[#F4D35E] to-[#E5C44F] rounded-xl flex items-center justify-center">
              <Trophy className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-[#1A1817]" style={{ fontFamily: "'Fredoka', sans-serif" }}>
              Dog Shows
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            <LanguageSwitcher variant="compact" />
            {user ? (
              <button 
                onClick={() => navigate('/puppoto/profile')}
                className="w-10 h-10 rounded-full bg-[#F5F3EE] flex items-center justify-center overflow-hidden"
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-full h-full object-cover" />
                ) : (
                  <User className="w-5 h-5 text-[#7A7571]" />
                )}
              </button>
            ) : (
              <button 
                onClick={handleGoogleLogin}
                className="w-10 h-10 rounded-full bg-[#FF7555] flex items-center justify-center"
              >
                <LogIn className="w-5 h-5 text-white" />
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#F4D35E]/20 to-[#FF7555]/10 py-12">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-[#1A1817] mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
            Dog Shows <span className="text-[#F4D35E]">Australia</span>
          </h1>
          <p className="text-lg text-[#7A7571] max-w-2xl mx-auto">
            Discover championship shows, breed competitions, and dog events 
            happening across Australia. Register your pup and compete!
          </p>
        </div>
      </section>

      {/* Filter Tabs */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex gap-2">
          {['upcoming', 'all'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-full font-medium capitalize transition ${
                filter === f
                  ? 'bg-[#FF7555] text-white'
                  : 'bg-white text-[#7A7571] hover:bg-[#F5F3EE]'
              }`}
            >
              {f === 'upcoming' ? 'Upcoming Shows' : 'All Shows'}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <main className="max-w-6xl mx-auto px-4 pb-12">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Shows Grid */}
          <div className="lg:col-span-2">
            {loading ? (
              <div className="flex justify-center py-20">
                <div className="animate-spin w-8 h-8 border-2 border-[#FF7555] border-t-transparent rounded-full" />
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {shows.map(show => (
                  <ShowCard
                    key={show.id}
                    show={show}
                    onViewDetails={setSelectedShow}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <ResultsSection results={DEMO_RESULTS} />

            {/* Quick Stats */}
            <div className="bg-white rounded-2xl p-6 shadow-[0_4px_20px_rgba(0,0,0,0.06)] border border-[#EBE8E0]">
              <h3 className="text-lg font-bold text-[#1A1817] mb-4" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Show Stats
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-[#F5F3EE] rounded-xl">
                  <p className="text-2xl font-bold text-[#FF7555]">150+</p>
                  <p className="text-xs text-[#7A7571]">Shows/Year</p>
                </div>
                <div className="text-center p-3 bg-[#F5F3EE] rounded-xl">
                  <p className="text-2xl font-bold text-[#4E876C]">200+</p>
                  <p className="text-xs text-[#7A7571]">Breeds</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Show Details Modal */}
      <AnimatePresence>
        {selectedShow && (
          <ShowDetailsModal
            show={selectedShow}
            isOpen={!!selectedShow}
            onClose={() => setSelectedShow(null)}
            user={user}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
